/**
 * Path of the first non-finite number found in `value`, or `null` if there is
 * none. Paths read like `numRows` or `boxTypes[0].width`.
 *
 * Space items are the motivating case. `NaN` (what an emptied number field
 * reports) and `Infinity` both serialize to `null` on the wire, and every
 * numeric field in the `Space` schemas is a plain `zod.number()` — so an item
 * persisted with one fails `Space.Item` parse on read-back, and readers skip
 * items that fail to parse (see `convertRoutineResponse`). The item and
 * everything inside it are then silently gone. Callers use this to refuse such
 * a write instead of destroying the stored item.
 */
export function findNonFiniteNumberPath(
  value: unknown,
  path = '',
): string | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? null : path || 'value';
  }

  if (Array.isArray(value)) {
    for (const [index, entry] of value.entries()) {
      const found = findNonFiniteNumberPath(entry, `${path}[${index}]`);

      if (found) {
        return found;
      }
    }

    return null;
  }

  if (value !== null && typeof value === 'object') {
    for (const [key, entry] of Object.entries(value)) {
      const found = findNonFiniteNumberPath(
        entry,
        path ? `${path}.${key}` : key,
      );

      if (found) {
        return found;
      }
    }
  }

  return null;
}
